import _, { add, forEach } from 'lodash';
import {useHistory, useParams} from 'react-router';

import {MutableRefObject, useContext, useEffect, useState} from 'react';

import {getTypeStorageUrlSuffixForEditScreen, useTypedFastStorage} from '../../../hooks/useFastStorage';
import useDirtyCheck, { setCleanStateStorage } from '../../../hooks/useDirtyCheck2';
import useJobMenu from '../../../hooks/useJobMenu';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import useGlobalYesNoDialog from '../../../hooks/useGlobalYesNoDialog';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import ApplicationContext from '../../../ApplicationContext';
import constants from '../../../constants';
import { appendLine, stringsContainsIgnoreCase} from '../../../util';
import {FormType} from '../../../fms.enum';
import fastStorage from "../../../fastStorage";
import {JobNames} from "../../../framework.core/JobNames";
import {compareIso, now, nowDate} from "../../../dateUtils";
import useSetEditedId from '../../../hooks/useSetEditedId';
import { ApiResult, InsertResultsWithData } from '../../../framework.core/data/ApiResult';
import { getSQLInfoList } from '../../../fms.core/data/TrackingModule';
import { TrackableEntity } from '../../../fms.core/data/ChangeTrackable';
import useSuntekSettings from '../../../hooks/useSuntekSettings';
import ReportSchedulingDetails from './ReportSchedulingDetails';
import { ReportSchHd } from '../../../fms.core/ReportSchHd';
import { TableColumn, TableSettings } from '../../../tableSettings';
import { CustomerShipmentListItem } from '../../../fms.data/models/CustomerShipmentListItem';
import * as XCol from '../../../framework.core/XExportColumn';
import { CheckLookup } from '../../../fms.data/lookup/CheckLookup';
import { TextValue } from '../../../framework.core/data/TextValue';
import { NavigationType, openNewPage } from '../../../util/navigationHelpers';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { replaceJobMenuTimestamp } from '../../../features/jobMenuSlice';
const CannotSaveAndContinueMarkup = <p>Cannot save Prepaid Expense Adjustment because Edit-Prepaid Expense Adjustment is already opened.<br/>Edit-Prepaid Expense Adjustment should be closed
  before save.</p>;

type ModeType = FormType.ModeAdd | FormType.ModeEdit | FormType.ModeView;

interface ReportSchedulingStorageKeys {
  modelStorage: string,
};

interface ReportSchedulingDetailsViewParams {
  mode: ModeType,
  id: string | undefined,
};

interface ReportSchedulingLoadedDetailsViewParams {
  mode: ModeType,
  id: number,
  goBack: VoidFunction,
  save: (x: boolean)=> Promise<boolean>,
  reportSch: ReportSchHd,
  reportSchRef: MutableRefObject<ReportSchHd | null>,
  setReportSch: (x: Partial<ReportSchHd>) => void,
  beforeRedirectToEdit: () => Promise<boolean>,
  isDirtyComparedTo: (x: any) => boolean,
  setCleanState: (x: any) => void,
  readonly: boolean,
  setReadonly: (x:boolean) => void,
  cancel:  VoidFunction,
  refresh: () => Promise<void>,
  settingColumn: TableSettings,
  setSettingColumn: (x: any) => void,
  defaultColumn: TableColumn[],
};

interface ReportSchedulingCheckData {
  u2Date: string,
  timeZone: string,
  startDate: string,
  sendTime: string,
  sendTimeChanged: string,
  endDate: string,
  isPaused: string,
  frequency: number,
}

function getReportSchedulingTitle(mode: ModeType) {
  switch (mode) {
    case FormType.ModeAdd:
      return 'New Report Schedule';
    case FormType.ModeEdit:
      return 'Edit Report Schedule';
    case FormType.ModeView:
      return 'View Report Schedule';
    default:
      throw new Error(`Unsupported mode ${mode}`);
  }
}

function getReportSchedulingStorageKeys(mode: ModeType, id: string | undefined): ReportSchedulingStorageKeys {
  switch (mode) {
    case FormType.ModeAdd:
      return {
        modelStorage: 'newReportScheduling',
      };
    case FormType.ModeEdit:
      if (!id) {
        throw new Error('idParam is missing');
      }
      return {
        modelStorage: `editReportScheduling${id}`,
      };
    case FormType.ModeView:
      if (!id) {
        throw new Error('idParam is missing');
      }
      return {
        modelStorage: `viewReportScheduling${id}`,
      };
    default:
      throw new Error(`Unsupported mode ${mode}`);
  }
}

interface ErrorFieldLabel {
  name: string,
  id: string,
};

const errorLabelFields:Array<ErrorFieldLabel> = [
  {name: 'Report Name', id: 'report-Name', },
  {name: 'Customer', id: 'customer', },
  {name: 'Recipient Emails', id: 'recipient-emails', },
  {name: 'Send Time', id: 'send-time', },
  {name: 'Start Date', id: 'start-date', },
  {name: 'End Date', id: 'end-date', },

];

function ReportSchedulingDetailsImpl(params: ReportSchedulingDetailsViewParams) {
  const {mode, id } = params;
  const title = getReportSchedulingTitle(mode);

  const { modelStorage } = getReportSchedulingStorageKeys(mode, id);

  const {setCleanState, isDirtyComparedTo, dirtyCleanup} = useDirtyCheck(modelStorage);

  const parsedId = !!id ? parseInt(id) : 0;

  const setSavedEntity = useSetEditedId([JobNames.ReportScheduling]);

  const [showGlobalOkDialog] = useGlobalOkDialog();

  const [initialized, setInitialized] = useState(false);

  const {goBack, removeJobMenu, hasOpenedScreenStartingWithUrl} = useJobMenu(title, constants.operations.shipmentStatus, null, true, cleanup, undefined, { inputId: 'pe-search-button', isLoading: initialized }, true /* no op suffix */, null, mode);

  const [schInStoarge, setSchInStorage, , schRef] = useTypedFastStorage<ReportSchHd>(modelStorage, null);

  const appContext = useContext(ApplicationContext);

  const [readonly, setReadonly, ] = useTypedFastStorage<boolean>(`reportSch-readonly-${mode}-${id}`, mode !== FormType.ModeAdd);

  const suntekSettings = useSuntekSettings();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const jobMenu = useAppSelector(state => state.jobMenu);

  function getFormatString(x: any){
    if(x.type === XCol.XCellType.Date) return suntekSettings.company?.dateFormat??"MM-dd-YYYY";
    if (['lbs','kgs','cbm'].includes(x.dataIndex)) return  "#,##0.00";
    if(x.dataIndex === 'pkgs') return "#,##0";
    return "";
  }
    
  const cols = CustomerShipmentListItem.getExportCollection();
  const defaultcols = cols.map<TableColumn>(x => {
    return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type, 
      formatString: getFormatString(x)};
  });
  const [settingColumn, setSettingColumn] = useState<TableSettings>({columns: defaultcols || []}); //변경된 Column Option 저장



  useEffect(() => {
      initialize();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  
  useFunctionKeys({
    onEsc: () => {
      if (initialized)
        return cancel();
      else return false;
    },
  });

  
  function cancel() {
    const modals = document.querySelectorAll('[class^="ReactModal__Content"]');
    if (modals.length > 0) {
      return false;
    }

    goBack();
    return true;
  }

    function copyCurrentModelToStorageForEditScreen(id: number) {
    const storageKeySuffix = getTypeStorageUrlSuffixForEditScreen(`${history.location.pathname}${history.location.search}`);
    const editModel = new ReportSchHd({...schInStoarge, id});
    editModel.trackable = true;

    TrackableEntity.acceptChanges(editModel, `editReportScheduling${id}`);

    fastStorage[`editReportScheduling${id}-${storageKeySuffix}`] = editModel;

    setCleanStateStorage(`editReportScheduling${id}-${storageKeySuffix}`, _.cloneDeep(editModel));
  }

  function validateEmails(input: string) {
    let message = "";
    const emails = input.split(';').map(s => s.trim()).filter(Boolean);
    if (input.includes(','))  message = '* Please separate email addresses with a semicolon (;).';

    const invalid = emails.filter(e => !/^[^\s@;,]+@[^\s@;,]+\.[^\s@;,]+$/.test(e));
    if (invalid.length > 0) message = `* Invalid email address: ${invalid.join(', ')}`;

    return message;
}

  /**
   * Check requirements
   * When critical errors exist, return false. Otherwise return true.
   */
   async function checkDataAsync():Promise<boolean> {
    if(!schRef.current){
       return true;
     }

     let errorMsg2 = "";

     if(!schRef.current.reportName){
        errorMsg2 = appendLine(errorMsg2, '* Empty ReportName');
     } 

     if(!schRef.current.recipientEmails) {
        errorMsg2 = appendLine(errorMsg2, '* Empty Receipient Emails');
     }

     if(!schRef.current.sendTime){
       errorMsg2 = appendLine(errorMsg2, '* Empty Send Time');
     }
     if(!schRef.current.startDate){
       errorMsg2 = appendLine(errorMsg2, '* Empty Start Date');
     }

     if(!schRef.current.endDate){
       errorMsg2 = appendLine(errorMsg2, '* Empty End Date');
     }

     if(schRef.current.recipientEmails.length > 0){
        const message =  validateEmails(schRef.current.recipientEmails);
        if(message)  errorMsg2 = appendLine(errorMsg2, message);
     }

     const sendTimeChanged = schRef.current.sendTime !== schRef.current.oldSendTime || schRef.current.startDate !== schRef.current.oldStartDate;

     const reqParam:ReportSchedulingCheckData = {
        u2Date: schInStoarge?.u2Date ?? '',
        sendTimeChanged: sendTimeChanged === true ? "1": "0",
        timeZone: schInStoarge?.timeZone ??'',
        startDate: schInStoarge?.startDate??'',
        sendTime: schInStoarge?.sendTime??'',
        endDate: schInStoarge?.endDate??'',
        frequency : schInStoarge?.frequency??1,
        isPaused: schInStoarge?.isPaused === true ? "1": "0",

     };

     const comLks: CheckLookup[] = [];
     if(schRef.current.customerName){
      comLks.push(new CheckLookup({
        labelName: "Customer",
        textValue: new TextValue({Text: schRef.current.customerName, IntValue: schRef.current.customerId})
      }))
     }

    const tempErrMsg = await appContext.api.checkCompanyLookupField(comLks);
    if(tempErrMsg) errorMsg2 = appendLine(errorMsg2, tempErrMsg);

    if(errorMsg2) {
      await showGlobalOkDialog(<p>Please check following information. (Specify the missing entries.)<br />
          {errorMsg2}
      </p>);
      focusFirstInvalidFieldBasedOn(errorMsg2);
      return false;
    }

    const chkResult:ReportSchedulingCheckData = await appContext.api.checkData(JobNames.ReportScheduling, schRef.current.id, reqParam);
    if(chkResult?.u2Date === "error"  ) {
       await showGlobalOkDialog(<p>Can not save this record because this record has been changed while you are editing..</p>, false, "Error");
       return false;
    }

    if (chkResult?.sendTime === "error") {
      await showGlobalOkDialog(<p>* Send Time has already passed.</p>, false, "Error");
      return false;
    }
    return true;
  }

  function focusFirstInvalidFieldBasedOn(errorMsg: string) {
    setTimeout(() => {
      const errorFieldMatch = errorLabelFields.find(x => stringsContainsIgnoreCase(errorMsg, x.name));
      if (errorFieldMatch) {
        const focusElement = document.getElementById(errorFieldMatch.id) as HTMLElement;
        if (focusElement) {
          focusElement.focus();
        }
      }
    }, 10);
  }

  async function savePea():Promise<ApiResult<InsertResultsWithData> | null> {
    if (!schInStoarge) {
      return null;
    }


    const isDataValid = await checkDataAsync();
     if (!isDataValid) {
       return null;
    }
    const additionalChange:Partial<ReportSchHd> = {};

    if(mode === FormType.ModeAdd) {      
      additionalChange.u1Id= appContext.user?.id??'';
      additionalChange.u1Date = now();
    }
    additionalChange.u2Id = appContext.user?.id??'';
    additionalChange.u2Date = now();

    if(schRef.current?.isColumnChange || mode === FormType.ModeAdd) {
      additionalChange.reportColumns = JSON.stringify(settingColumn.columns);
    }

    setSchInStorage(additionalChange);

  
    const sqlInfoList = getSQLInfoList(schRef.current as TrackableEntity);

    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveJobDetail(
      JobNames.ReportScheduling, 
      sqlInfoList,
      {
        timezone: schRef.current?.timeZone,
        sendTime: schRef.current?.sendTime,
        startDate: schRef.current?.startDate,
        endDate: schRef.current?.endDate,
        frequency: schRef.current?.frequency,
        id: schRef.current?.id,
        isPaused: schRef.current?.isPaused
      }
    );

    if (apiResult && apiResult.success) {
      let storageUpdate: Partial<ReportSchHd> = {};

      if(mode === FormType.ModeAdd) {
        const insertResult = apiResult.result.insertList.find(x => x.model === 'ReportSchHd');
        const savedId = insertResult?.newId ?? 0;

        storageUpdate.id = savedId;
      }

      if (!_.isEmpty(storageUpdate)) {
        setSchInStorage(storageUpdate);
      }

      return apiResult;
    } else {
      await showGlobalOkDialog(<p>Error saving data:<br />{apiResult?.message}</p>)
    }

    return null;
  }

  async function loadExisting() {
    const apiResult = await appContext.api.getJobDetail(JobNames.ReportScheduling, { id });

    if (!apiResult || !apiResult.success) {
      const errorMessage = apiResult?.message || 'ReportScheduling not found';
      await showGlobalOkDialog(<p>{errorMessage}</p>);
      goBack();
      return;
    }

    let columns : TableColumn[] = [];

    try {
      columns = JSON.parse(apiResult.result.reportColumns); //저장된 column 정보로 저장
    } catch {
      columns = defaultcols;
    }

    setSettingColumn({columns: columns}); 


    
    setCleanState(new ReportSchHd(apiResult.result));

    const model = new ReportSchHd(_.cloneDeep(apiResult.result));
    model.oldEndDate = model.endDate;
    model.oldStartDate = model.startDate;
    model.oldSendTime = model.sendTime;

    setSchInStorage(model);

    if (schRef.current) {
      schRef.current.trackable = true;
    }
  }

  async function initialize(isRefresh: boolean = false) {
    if (!schInStoarge || isRefresh) {
      if (mode === FormType.ModeAdd) {
        const newModel = new ReportSchHd();
        newModel.shipmentType = 'OI';
        newModel.startDate = nowDate();
        newModel.oldStartDate = nowDate();
        newModel.timeZone = 'Pacific Standard Time';
        newModel.frequency = 1;
        newModel.templateName = 'Email Signature'; //Settings> E-Mail Template Name
        
        setSchInStorage(newModel);
        setCleanState(newModel);

        if(schRef.current) {
          schRef.current.trackable = true;

        }

      } else {
        await loadExisting();
      }
    }

    setInitialized(true);
  }

  async function refresh(){
      initialize(true);
  }

  function OpenNewPageAsync(link:string) {
    openNewPage(history, link, NavigationType.newPage);
    return Promise.resolve(true);
  }

  async function doSave(isPreview:boolean): Promise<boolean> {

    if (!schInStoarge || !schRef.current) {
      throw new Error('Invalid operation');
    }

    if(!schRef.current?.isDirty && !schRef.current?.isColumnChange) {
      if(!isPreview) await showGlobalOkDialog(<p>Nothing to save!!!</p>);
      return true;
    }

    if (mode === FormType.ModeAdd && hasOpenedScreenStartingWithUrl('/shipmentStatus/reportScheduling/edit')) {
      await showGlobalOkDialog(CannotSaveAndContinueMarkup);
      return false;
    }
    const apiResult = await savePea();

    if (!apiResult) {
      return false;
    }

    let savedId = schRef.current?.id ?? 0;
    if (mode === FormType.ModeAdd) {
      const insertResult = apiResult.result.insertList.find(x => x.model === 'ReportSchHd');
      savedId = insertResult?.newId ?? 0;
    }

    if (apiResult) {
      setSavedEntity(savedId.toString(), mode === FormType.ModeAdd);
      if(!isPreview) await showGlobalOkDialog(<p>The data has been saved</p>);
    }
    if (mode === FormType.ModeAdd) {
     
        copyCurrentModelToStorageForEditScreen(savedId);

        const editLink = `/shipmentStatus/reportScheduling/edit/${savedId}`;
        await OpenNewPageAsync(editLink);
        const addModeTimestamp = jobMenu[String('shipmentStatus')].find(y => (y.url === '/shipmentStatus/reportScheduling/add'))?.timestamp;
        if (addModeTimestamp)
          dispatch(replaceJobMenuTimestamp({ url: editLink, timestamp: addModeTimestamp }));
      
      removeJobMenu();
    } 
    else {
      setCleanState(_.cloneDeep(schInStoarge));
    }

    return true;
  }
  
  function save(isPreview: boolean) {
    return doSave(isPreview);
  }

  function beforeRedirectToEdit() {
    removeJobMenu();
    return Promise.resolve(true);
  }

  function cleanup() {
    dirtyCleanup();
  }

  if (!initialized || !schInStoarge) {
    return null;
  }

  return (
    <ReportSchedulingDetailsInitializedImpl
      goBack={goBack}
      readonly={readonly ?? false}
      setReadonly={setReadonly}
      save={save}
      mode={mode}
      id={parsedId}
      reportSch={schInStoarge}
      reportSchRef={schRef}
      setReportSch={setSchInStorage}
      beforeRedirectToEdit={beforeRedirectToEdit}
      isDirtyComparedTo={isDirtyComparedTo}
      setCleanState={setCleanState}
      cancel={cancel}
      refresh={refresh}
      settingColumn={settingColumn}
      setSettingColumn={setSettingColumn}
      defaultColumn={defaultcols}
    />
  );
}

function ReportSchedulingDetailsInitializedImpl({
                                                          mode,
                                                          id,
                                                          goBack,
                                                          save,
                                                          reportSch,
                                                          reportSchRef,
                                                          setReportSch,
                                                          beforeRedirectToEdit,
                                                          isDirtyComparedTo,
                                                          setCleanState,
                                                          readonly,
                                                          setReadonly,
                                                          cancel,
                                                          refresh,
                                                          settingColumn,
                                                          setSettingColumn,
                                                          defaultColumn
                                                        
                                                        }: ReportSchedulingLoadedDetailsViewParams) {
  const title = getReportSchedulingTitle(mode);
  const appContext = useContext(ApplicationContext);
  //const listSec = findSecurityTaskWithBranch(appContext, FmsSecTask.AcctPrepaidAdjustment, prepaidExpenseAdjustment.branch);
  const suntekSettings = useSuntekSettings();

  //const isAmend = !readonly;

    return (
    <ReportSchedulingDetails
        title={title}
        save={save}
        reportSch={reportSch}
        onChange={setReportSch}
        isView={mode === FormType.ModeView}
        isEdit={mode === FormType.ModeEdit} 
        cancel={cancel}
        settingColumn={settingColumn}
        setSettingColumn={setSettingColumn}
        defaultColumn={defaultColumn}
        />
  );
}

const ReportSchedulingView = () => {
  const {mode, id} = useParams<ReportSchedulingDetailsViewParams>();

  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */
  return (
    <ReportSchedulingDetailsImpl mode={mode} id={id} key={`reportScheduling-${mode}-${id}-details`} />
  );
};
export default ReportSchedulingView;
